import React, { useState, useEffect } from "react";
import {
  FaEye,
  FaEyeSlash,
  FaExclamationCircle,
  FaArrowRight
} from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./../assets/scss/login.scss";
import AuthFooter from "../components/layout/footer/AuthFooter";
import AuthMechanism from "../components/auth/AuthMechanism";
import AuthHeader from "../components/auth/AuthHeader";
import { showToast } from "../utils/utils";
import publicAxios from "../utils/publicAxios";
import { getConfirmForgetPasswordURL } from "../utils/constants/apiEndPoints";
import { LOGIN } from "../route/paths";

/**
 * Set-new-password page (/password/reset-link) — the step after the user clicks
 * the emailed link. Shares the exact auth shell + field vocabulary as login /
 * sign-up / send-reset / OTP so the whole flow is one consistent surface.
 */
const ResetPassword = () => {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailParams = queryParams.get("email");
  const tokenParams = queryParams.get("token");
  const decodedEmail = emailParams
    ? decodeURIComponent(emailParams).replace(/ /g, "+")
    : "";

  useEffect(() => {
    if (emailParams && tokenParams) {
      setIsValid(true);
      setEmail(decodedEmail);
      setToken(tokenParams);
    } else {
      setIsValid(false);
    }
  }, [emailParams, tokenParams, decodedEmail]);

  const initialErrors = { password: [], confirmPassword: [] };
  const [errors, setErrors] = useState(initialErrors);

  const navigate = useNavigate();

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setErrors((prev) => ({ ...prev, password: [] }));
  };
  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setErrors((prev) => ({ ...prev, confirmPassword: [] }));
  };
  const handleClickShowPassword = () => setShowPassword((s) => !s);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let ok = true;
    const newErrors = { password: [], confirmPassword: [] };
    if (!password) {
      newErrors.password = ["Please enter a new password"];
      ok = false;
    }
    if (password !== confirmPassword) {
      newErrors.confirmPassword = ["Passwords do not match"];
      ok = false;
    }
    setErrors(newErrors);
    if (!ok) return;

    setLoading(true);
    try {
      const response = await publicAxios.post(getConfirmForgetPasswordURL, {
        email,
        password,
        confirmPassword,
        token
      });
      if (response.status === 200) {
        showToast(response.data.message, "success");
        navigate(LOGIN);
        setPassword("");
        setConfirmPassword("");
        setErrors(initialErrors);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        const apiErrors = error.response.data.errors?.[0];
        if (apiErrors) setErrors(apiErrors);
        setPassword("");
        setConfirmPassword("");
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            "Something went wrong. Please try again."
        );
        setErrors(initialErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-viewport">
      <AuthHeader />
      <main className="auth-page">
        <div className="auth-layout">
          <AuthMechanism who="mjwebs" />
          <div className="auth-right-panel">
            <div className="auth-form-wrap">
              {!isValid ? (
                <>
                  <h1 className="auth-heading">Link expired or invalid</h1>
                  <p className="auth-subheading">
                    This reset link is missing or has expired. Request a fresh
                    one from the login page.
                  </p>
                  <p className="auth-toggle-text auth-toggle-text--left">
                    <Link to={LOGIN}>Back to login</Link>
                  </p>
                </>
              ) : (
                <>
                  <h1 className="auth-heading">Set a new password</h1>
                  <p className="auth-subheading">
                    Choose a new password for <strong>{email}</strong>.
                  </p>

                  {errorMessage && (
                    <div className="auth-form-error">
                      <p>{errorMessage}</p>
                    </div>
                  )}

                  <form
                    id="reset_form"
                    className="auth-page-form"
                    onSubmit={handleSubmit}
                  >
                    <div className="input-row">
                      <div className="input-group">
                        <label htmlFor="email">Email address</label>
                        <input
                          type="email"
                          value={email}
                          name="email"
                          id="email"
                          autoComplete="username"
                          disabled
                        />
                      </div>
                    </div>

                    <div className="input-row">
                      <div className="input-group">
                        <label htmlFor="password">New password</label>
                        <div className="passowrd-show">
                          <input
                            type={showPassword ? "text" : "password"}
                            onChange={handlePasswordChange}
                            value={password}
                            name="password"
                            id="password"
                            placeholder="At least 8 characters"
                            autoComplete="new-password"
                            aria-invalid={errors.password?.length > 0}
                          />
                          <button
                            type="button"
                            className="password-icon"
                            onClick={handleClickShowPassword}
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                        {Array.isArray(errors.password) &&
                          errors.password.map((msg, index) => (
                            <p key={index} className="error-message">
                              <FaExclamationCircle
                                className="error-icon"
                                aria-hidden
                              />
                              <small>{msg}</small>
                            </p>
                          ))}
                      </div>
                    </div>

                    <div className="input-row">
                      <div className="input-group">
                        <label htmlFor="confirmPassword">
                          Confirm new password
                        </label>
                        <input
                          type={showPassword ? "text" : "password"}
                          onChange={handleConfirmPasswordChange}
                          value={confirmPassword}
                          name="confirmPassword"
                          id="confirmPassword"
                          placeholder="Re-enter your password"
                          autoComplete="new-password"
                          aria-invalid={errors.confirmPassword?.length > 0}
                        />
                        {Array.isArray(errors.confirmPassword) &&
                          errors.confirmPassword.map((msg, index) => (
                            <p key={index} className="error-message">
                              <FaExclamationCircle
                                className="error-icon"
                                aria-hidden
                              />
                              <small>{msg}</small>
                            </p>
                          ))}
                      </div>
                    </div>

                    <div className="input-row">
                      <div className="input-group">
                        <button
                          type="submit"
                          className="auth-btn-secondary"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span
                                className="auth-spinner"
                                aria-hidden="true"
                              />
                              Saving…
                            </>
                          ) : (
                            <>
                              Reset password <FaArrowRight />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>

                  <p className="auth-toggle-text">
                    Remembered it? <Link to={LOGIN}>Log in</Link>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <AuthFooter />
    </div>
  );
};

export default ResetPassword;
